import React from 'react';
import { Box, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HistoryIcon from '@mui/icons-material/History';
import SyncIcon from '@mui/icons-material/Sync';
import {
  TransferJob,
  TransferJobStatus,
  useTransferManagerContext,
} from '../../../context/TransferManagerContext';

const TYPE_ICON: Record<TransferJob['type'], React.ReactNode> = {
  download: <DownloadIcon sx={{ fontSize: 14 }} />,
  sync: <SyncIcon sx={{ fontSize: 14 }} />,
  create: <AddCircleOutlineIcon sx={{ fontSize: 14 }} />,
};

function timeAgo(ts: number): string {
  const diffSec = Math.floor((Date.now() - ts) / 1000);
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  return `${diffHr}h ago`;
}

const HistoryRow: React.FC<{ job: TransferJob }> = ({ job }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      py: 0.75,
      '&:not(:last-child)': {
        borderBottom: '1px solid',
        borderColor: 'divider',
      },
    }}
  >
    <Box sx={{ color: 'text.disabled', display: 'flex', flexShrink: 0 }}>
      {TYPE_ICON[job.type]}
    </Box>

    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Typography
        variant="caption"
        display="block"
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {job.projectName}
      </Typography>
      <Typography
        variant="caption"
        color="text.disabled"
        sx={{ fontSize: '0.6rem' }}
      >
        {job.totalFiles} file{job.totalFiles !== 1 ? 's' : ''}
        {job.completedAt ? ` · ${timeAgo(job.completedAt)}` : ''}
      </Typography>
    </Box>

    {job.status === TransferJobStatus.COMPLETE && (
      <CheckCircleOutlineIcon
        sx={{ fontSize: 14, color: 'success.main', flexShrink: 0 }}
      />
    )}
    {job.status === TransferJobStatus.ERROR && (
      <ErrorOutlineIcon
        sx={{ fontSize: 14, color: 'error.main', flexShrink: 0 }}
      />
    )}
    {job.status === TransferJobStatus.CANCELLED && (
      <BlockIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
    )}
  </Box>
);

export const TransferHistoryPanel: React.FC = () => {
  const { history, clearHistory } = useTransferManagerContext();

  return (
    <Box>
      <Divider />
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 0.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <HistoryIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ lineHeight: 1 }}
          >
            History
          </Typography>
        </Box>
        {history.length > 0 && (
          <Tooltip title="Clear history">
            <IconButton
              size="small"
              onClick={clearHistory}
              sx={{ p: 0.25 }}
              aria-label="Clear transfer history"
            >
              <DeleteSweepIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ px: 2, pb: 2, maxHeight: 200, overflowY: 'auto' }}>
        {history.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              py: 2,
              borderRadius: '8px',
              bgcolor: 'studio.surfaces.fillWeak',
            }}
          >
            <Typography variant="caption" color="text.disabled" align="center">
              No recent transfers
            </Typography>
          </Box>
        ) : (
          history.map((job) => <HistoryRow key={job.id} job={job} />)
        )}
      </Box>
    </Box>
  );
};
